import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, ShoppingBag, TrendingUp, Calendar, Settings, 
  Search, Upload, FileText, Music, ChevronRight, LogOut,
  DollarSign, UserPlus, Activity, AlertCircle, Mic, 
  Printer, Package, Bell, Edit3, Check, Play, Save,
  RefreshCw, PauseCircle, RotateCcw, X, MessageSquare, Send,
  Phone, Mail, Clock, CreditCard, ArrowLeft, Download, HelpCircle
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import './AdminPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatAddress(a) {
  if (!a) return '';
  const parts = [a.street, a.city, a.state, a.postcode, a.country].filter((p) => p && p.trim());
  return parts.join(', ');
}

function SignupDetailPanel({ order }) {
  const cellTitle = { fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#8a8378', marginBottom: 4 };
  const cellValue = { fontSize: 14, color: '#3a3530', lineHeight: 1.55 };
  const empty = <em style={{ color: '#aaa', fontStyle: 'italic' }}>not yet provided</em>;
  const lovedAddr = formatAddress(order.loved_one_address);
  const custAddr = formatAddress(order.customer_address);
  const onboardingUrl = order.onboarding_token
    ? `${window.location.origin}/legacy/onboarding/${order.onboarding_token}`
    : '';
  return (
    <div data-testid={`signup-detail-${order.id}`}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem 2rem', marginBottom: '1rem' }}>
        <div>
          <div style={cellTitle}>Customer</div>
          <div style={cellValue}>
            {order.customer_full_name || empty}
            <div style={{ fontSize: 12, color: '#888' }}>{order.user_email || ''}</div>
          </div>
        </div>
        <div>
          <div style={cellTitle}>Customer mailing address</div>
          <div style={cellValue}>{custAddr || empty}</div>
        </div>
        <div>
          <div style={cellTitle}>Loved one</div>
          <div style={cellValue}>
            {order.loved_one_full_name || empty}
            {order.loved_one_preferred_name && (
              <div style={{ fontSize: 13, color: '#5c4a3d', fontStyle: 'italic' }}>
                Addressed as &ldquo;{order.loved_one_preferred_name}&rdquo;
              </div>
            )}
            {order.relationship && (
              <div style={{ fontSize: 12, color: '#888' }}>{order.relationship}</div>
            )}
          </div>
        </div>
        <div>
          <div style={cellTitle}>Loved one phone</div>
          <div style={cellValue}>{order.loved_one_phone || empty}</div>
        </div>
        <div>
          <div style={cellTitle}>Loved one address <span style={{ fontSize: 10, color: '#aaa', textTransform: 'none', letterSpacing: 0 }}>(for hello card)</span></div>
          <div style={cellValue}>{lovedAddr || empty}</div>
        </div>
        <div>
          <div style={cellTitle}>Stage</div>
          <div style={cellValue}>
            {order.lifecycle_status === 'awaiting_onboarding' && 'Awaiting onboarding'}
            {order.lifecycle_status === 'awaiting_first_call' && 'Awaiting first call'}
            {order.lifecycle_status === 'in_progress' && 'In progress — letters being captured'}
            {order.lifecycle_status === 'completed' && 'Completed'}
            {!['awaiting_onboarding', 'awaiting_first_call', 'in_progress', 'completed'].includes(order.lifecycle_status) && (order.lifecycle_status || '\u2014')}
          </div>
        </div>
      </div>

      {order.notes && (
        <div style={{ marginTop: '0.75rem', padding: '0.85rem 1rem', background: '#fff', border: '1px solid #eee', borderRadius: 6 }}>
          <div style={cellTitle}>Notes from customer</div>
          <div style={{ ...cellValue, whiteSpace: 'pre-wrap' }}>{order.notes}</div>
        </div>
      )}

      {!order.onboarding_complete && onboardingUrl && (
        <div style={{ marginTop: '0.85rem', padding: '0.65rem 0.85rem', background: '#fff7e6', border: '1px solid #f1d9a6', borderRadius: 6, fontSize: 12.5, color: '#7a5a14' }}>
          Customer hasn&apos;t completed onboarding yet. Their private link:{' '}
          <a href={onboardingUrl} target="_blank" rel="noreferrer" style={{ color: '#8b9a7d', wordBreak: 'break-all' }}>{onboardingUrl}</a>
        </div>
      )}
    </div>
  );
}

function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('production');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [productionStats, setProductionStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [signupData, setSignupData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [churnData, setChurnData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [earlyBirdCount, setEarlyBirdCount] = useState(72);
  
  // Production Hub states
  const [editorsDesk, setEditorsDesk] = useState([]);
  const [plottingBatch, setPlottingBatch] = useState([]);
  const [assemblyLine, setAssemblyLine] = useState([]);
  const [nudgesNeeded, setNudgesNeeded] = useState([]);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editedTranscript, setEditedTranscript] = useState('');
  
  // Refund/Pause states
  const [refundModal, setRefundModal] = useState(null);
  const [refundType, setRefundType] = useState('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [pauseModal, setPauseModal] = useState(null);
  const [pauseMonths, setPauseMonths] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);

  // SMS states
  const [smsLogs, setSmsLogs] = useState([]);

  // Call Logs states
  const [callLogs, setCallLogs] = useState([]);

  // Activity Feed states
  const [activityFeed, setActivityFeed] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);

  // Leads states
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);

  // Legacy orders states
  const [legacyOrders, setLegacyOrders] = useState([]);
  const [legacySummary, setLegacySummary] = useState(null);

  // Newsletter states
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [newsletterHistory, setNewsletterHistory] = useState([]);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterBody, setNewsletterBody] = useState('');
  const [newsletterTestEmail, setNewsletterTestEmail] = useState('admin@bindingthread.com.au');
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [newsletterConfirmOpen, setNewsletterConfirmOpen] = useState(false);

  // Signups (legacy orders) - expanded row tracking
  const [expandedSignupId, setExpandedSignupId] = useState(null);

  // Check for existing token
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, { password });
      setToken(response.data.access_token);
      localStorage.setItem('admin_token', response.data.access_token);
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setIsAuthenticated(false);
    localStorage.removeItem('admin_token');
  };

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Always load production stats
      if (activeTab === 'production' || activeTab === 'dashboard' || !productionStats) {
        try {
          const prodStatsRes = await axios.get(`${API_URL}/api/admin/production/stats`, authHeaders);
          setProductionStats(prodStatsRes.data);
        } catch (e) {
          console.log('Production stats not available yet');
        }
      }
      
      if (activeTab === 'production') {
        const [editorRes, plotRes, assemblyRes, nudgeRes, recordingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/production/editors-desk`, authHeaders),
          axios.get(`${API_URL}/api/admin/production/plotting-batch`, authHeaders),
          axios.get(`${API_URL}/api/admin/production/assembly-line`, authHeaders),
          axios.get(`${API_URL}/api/admin/production/nudges-needed`, authHeaders),
          axios.get(`${API_URL}/api/admin/voice/recordings`, authHeaders).catch(() => ({ data: { recordings: [] } }))
        ]);
        setEditorsDesk(editorRes.data.subscriptions || []);
        setPlottingBatch(plotRes.data.subscriptions || []);
        setAssemblyLine(assemblyRes.data.subscriptions || []);
        setNudgesNeeded(nudgeRes.data.subscriptions || []);
        setAvailableRecordings(recordingsRes.data.recordings || []);
      }
      
      if (activeTab === 'dashboard' || !stats) {
        const statsRes = await axios.get(`${API_URL}/api/admin/stats`, authHeaders);
        setStats(statsRes.data);
        setEarlyBirdCount(statsRes.data.early_bird_remaining);
      }
      
      if (activeTab === 'customers') {
        const customersRes = await axios.get(
          `${API_URL}/api/admin/customers?search=${searchQuery}`, 
          authHeaders
        );
        setCustomers(customersRes.data.customers);
        setCustomersTotal(customersRes.data.total);
      }
      
      if (activeTab === 'subscriptions' || activeTab === 'sms') {
        const subsRes = await axios.get(`${API_URL}/api/admin/subscriptions`, authHeaders);
        setSubscriptions(subsRes.data.subscriptions);
      }
      
      if (activeTab === 'orders') {
        const ordersRes = await axios.get(`${API_URL}/api/admin/orders`, authHeaders);
        setOrders(ordersRes.data.orders);
      }
      
      if (activeTab === 'reports') {
        const [signupsRes, revenueRes, churnRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/reports/signups`, authHeaders),
          axios.get(`${API_URL}/api/admin/reports/revenue`, authHeaders),
          axios.get(`${API_URL}/api/admin/reports/churn`, authHeaders)
        ]);
        setSignupData(signupsRes.data.data);
        setRevenueData(revenueRes.data.data);
        setChurnData(churnRes.data.data);
      }

      if (activeTab === 'sms') {
        const logsRes = await axios.get(`${API_URL}/api/admin/sms/logs`, authHeaders);
        setSmsLogs(logsRes.data.logs || []);
      }

      if (activeTab === 'calls') {
        const callLogsRes = await axios.get(`${API_URL}/api/admin/voice/call-logs`, authHeaders);
        setCallLogs(callLogsRes.data.logs || []);
      }

      if (activeTab === 'activity') {
        const actRes = await axios.get(`${API_URL}/api/admin/activity-feed?limit=100`, authHeaders);
        setActivityFeed(actRes.data.activities || []);
        setActivityTotal(actRes.data.total || 0);
      }

      if (activeTab === 'leads') {
        const leadsRes = await axios.get(`${API_URL}/api/admin/leads`, authHeaders);
        setLeads(leadsRes.data.leads || []);
        setLeadsTotal(leadsRes.data.total || 0);
      }

      if (activeTab === 'legacy') {
        const legRes = await axios.get(`${API_URL}/api/admin/legacy-orders`, authHeaders);
        setLegacyOrders(legRes.data.orders || []);
        setLegacySummary(legRes.data.summary || null);
      }

      if (activeTab === 'newsletter') {
        const [previewRes, histRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/newsletter/preview`, authHeaders),
          axios.get(`${API_URL}/api/admin/newsletter/history`, authHeaders),
        ]);
        setNewsletterPreview(previewRes.data);
        setNewsletterHistory(histRes.data.sends || []);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Legacy order actions
  const runLegacyNudgeCheck = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/legacy-orders/run-nudge-check`,
        {},
        authHeaders
      );
      toast.success(res.data?.message || 'Legacy nudge check complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run nudge check');
    }
  };

  const markLegacyLetterDelivered = async (legacyOrderId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/legacy-orders/${legacyOrderId}/mark-letter-delivered`,
        {},
        authHeaders
      );
      toast.success('Letter marked as delivered');
      const res = await axios.get(`${API_URL}/api/admin/legacy-orders`, authHeaders);
      setLegacyOrders(res.data.orders || []);
      setLegacySummary(res.data.summary || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark letter delivered');
    }
  };

  const undoLegacyLetter = async (legacyOrderId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/legacy-orders/${legacyOrderId}/undo-letter`,
        {},
        authHeaders
      );
      toast.success('Last delivery undone');
      const res = await axios.get(`${API_URL}/api/admin/legacy-orders`, authHeaders);
      setLegacyOrders(res.data.orders || []);
      setLegacySummary(res.data.summary || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo');
    }
  };

  const loadCustomerDetail = async (customerId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/customers/${customerId}`,
        authHeaders
      );
      setSelectedCustomer(response.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const handleFileUpload = async (customerId, file, fileType, month, sendNotification = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    formData.append('month', month);
    formData.append('send_notification', sendNotification);

    try {
      await axios.post(
        `${API_URL}/api/admin/customers/${customerId}/upload`,
        formData,
        { ...authHeaders, headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(sendNotification ? 'File uploaded & notification sent!' : 'File uploaded successfully');
      loadCustomerDetail(customerId);
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const updateEarlyBirdCounter = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/settings/early-bird?count=${earlyBirdCount}`,
        {},
        authHeaders
      );
      toast.success('Early Bird counter updated');
    } catch (error) {
      toast.error('Failed to update counter');
    }
  };

  // Production Hub Functions
  const [transcribing, setTranscribing] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [availableRecordings, setAvailableRecordings] = useState([]);

  const linkRecording = async (subscriptionId, recordingUrl) => {
    try {
      await axios.post(`${API_URL}/api/admin/production/link-recording`,
        { subscription_id: subscriptionId, recording_url: recordingUrl },
        authHeaders
      );
      toast.success('Recording linked!');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to link recording');
    }
  };

  const transcribeRecording = async (subscriptionId, recordingUrl) => {
    setTranscribing(subscriptionId);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/transcribe`,
        { recording_url: recordingUrl, subscription_id: subscriptionId },
        authHeaders
      );
      setTranscriptText(res.data.transcript);
      setEditingSubscription(subscriptionId);
      toast.success('Transcription complete!');
    } catch (error) {
      toast.error('Transcription failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setTranscribing(null);
    }
  };

  const loadTranscript = async (subscriptionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/transcript/${subscriptionId}`, authHeaders);
      if (res.data.transcript) {
        setTranscriptText(res.data.transcript.transcript);
        setEditingSubscription(subscriptionId);
      } else {
        toast.info('No transcript found for this subscription');
      }
    } catch (error) {
      toast.error('Failed to load transcript');
    }
  };

  const saveTranscript = async (subscriptionId) => {
    setSavingTranscript(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/transcript/save`,
        { subscription_id: subscriptionId, transcript: transcriptText },
        authHeaders
      );
      toast.success('Transcript saved');
    } catch (error) {
      toast.error('Failed to save transcript');
    } finally {
      setSavingTranscript(false);
    }
  };

  const exportSvg = async (subscriptionId, page = 1) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/transcript/${subscriptionId}/export-svg?page=${page}`,
        {},
        { ...authHeaders, responseType: 'blob' }
      );
      const pageSuffix = page > 1 ? `-p${page}` : '';
      const blob = new Blob([res.data], { type: 'image/svg+xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `binding-thread-letter-${subscriptionId.slice(0, 8)}${pageSuffix}.svg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Page ${page} SVG exported!`);
    } catch (error) {
      toast.error('Export failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const exportPdf = async (subscriptionId) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/transcript/${subscriptionId}/export-pdf`,
        {},
        { ...authHeaders, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `binding-thread-letter-${subscriptionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported — ready for the home printer');
    } catch (error) {
      toast.error('PDF export failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const approveTranscript = async (subscriptionId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/production/approve-transcript`,
        { subscription_id: subscriptionId, human_edited_transcript: editedTranscript },
        authHeaders
      );
      toast.success('Transcript approved - moved to Ready to Plot');
      setEditingSubscription(null);
      setEditedTranscript('');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to approve transcript');
    }
  };

  const markDispatched = async (subscriptionIds) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/production/mark-dispatched`,
        { subscription_ids: subscriptionIds, new_status: 'dispatched' },
        authHeaders
      );
      toast.success(`${subscriptionIds.length} letter(s) marked as dispatched - emails sent!`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to mark as dispatched');
    }
  };

  const bulkUpdateStatus = async (subscriptionIds, newStatus) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/production/bulk-status-update`,
        { subscription_ids: subscriptionIds, new_status: newStatus },
        authHeaders
      );
      toast.success(`Updated ${subscriptionIds.length} subscription(s)`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRefund = async () => {
    if (!refundModal) return;
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/subscription/refund`,
        {
          subscription_id: refundModal.id,
          refund_type: refundType,
          amount: refundType === 'partial' ? parseFloat(refundAmount) : null,
          reason: refundReason
        },
        authHeaders
      );
      toast.success(response.data.message);
      setRefundModal(null);
      setRefundType('full');
      setRefundAmount('');
      setRefundReason('');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    if (!pauseModal) return;
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/subscription/pause`,
        { subscription_id: pauseModal.id, pause_months: pauseMonths },
        authHeaders
      );
      toast.success(response.data.message);
      setPauseModal(null);
      setPauseMonths(1);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async (subscriptionId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/subscription/resume`,
        { subscription_id: subscriptionId },
        authHeaders
      );
      toast.success(response.data.message);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const sendWelcomeSms = async (subscriptionId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/sms/send-welcome?subscription_id=${subscriptionId}`,
        {},
        authHeaders
      );
      toast.success(response.data.message);
      if (activeTab === 'sms') loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setActionLoading(false);
    }
  };

  const sendNudgeSms = async (subscriptionId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/sms/send-nudge?subscription_id=${subscriptionId}`,
        {},
        authHeaders
      );
      toast.success(response.data.message);
      if (activeTab === 'sms') loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setActionLoading(false);
    }
  };

  const runNudgeCheck = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/sms/run-nudge-check`,
        {},
        authHeaders
      );
      toast.success(response.data.message);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run nudge check');
    } finally {
      setActionLoading(false);
    }
  };

  const getLifecycleColor = (status) => {
    switch (status) {
      case 'awaiting_audio': return '#f59e0b';
      case 'audio_received': return '#3b82f6';
      case 'ready_to_plot': return '#8b5cf6';
      case 'dispatched': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getLifecycleLabel = (status) => {
    switch (status) {
      case 'awaiting_audio': return 'Awaiting Audio';
      case 'audio_received': return 'Audio Received';
      case 'ready_to_plot': return 'Ready to Plot';
      case 'dispatched': return 'Dispatched';
      default: return status;
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login" data-testid="admin-login">
        <div className="login-card">
          <h1>Admin Dashboard</h1>
          <p>Enter admin password to continue</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin Password"
              data-testid="admin-password-input"
            />
            <button type="submit" disabled={loading} data-testid="admin-login-btn">
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>Admin</h2>
          <button onClick={handleLogout} className="logout-btn" title="Logout">
            <LogOut size={18} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <button 
            className={activeTab === 'production' ? 'active' : ''} 
            onClick={() => setActiveTab('production')}
          >
            <Package size={18} /> Production Hub
          </button>
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''} 
            onClick={() => setActiveTab('dashboard')}
          >
            <Activity size={18} /> Dashboard
          </button>
          <button 
            className={activeTab === 'customers' ? 'active' : ''} 
            onClick={() => setActiveTab('customers')}
          >
            <Users size={18} /> Customers
          </button>
          <button 
            className={activeTab === 'reports' ? 'active' : ''} 
            onClick={() => setActiveTab('reports')}
          >
            <TrendingUp size={18} /> Reports
          </button>
          <button 
            className={activeTab === 'sms' ? 'active' : ''} 
            onClick={() => setActiveTab('sms')}
          >
            <MessageSquare size={18} /> SMS
          </button>
          <button 
            className={activeTab === 'activity' ? 'active' : ''} 
            onClick={() => setActiveTab('activity')}
            data-testid="activity-tab-btn"
          >
            <Clock size={18} /> Activity Feed
          </button>
          <button 
            className={activeTab === 'leads' ? 'active' : ''} 
            onClick={() => setActiveTab('leads')}
            data-testid="leads-tab-btn"
          >
            <Mail size={18} /> Email Leads
          </button>
          <button 
            className={activeTab === 'newsletter' ? 'active' : ''} 
            onClick={() => setActiveTab('newsletter')}
            data-testid="newsletter-tab-btn"
          >
            <Send size={18} /> Newsletter
          </button>
          <button
            className={activeTab === 'legacy' ? 'active' : ''}
            onClick={() => setActiveTab('legacy')}
            data-testid="legacy-tab-btn"
          >
            <Package size={18} /> Signups
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''} 
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={18} /> Settings
          </button>
          <button 
            className={activeTab === 'help' ? 'active' : ''} 
            onClick={() => setActiveTab('help')}
            data-testid="help-tab-btn"
          >
            <HelpCircle size={18} /> Plotter Guide
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {loading && <div className="loading-overlay">Loading...</div>}

        {/* Production Hub Tab */}
        {activeTab === 'production' && (
          <div className="production-content">
            <h1>Production Hub</h1>
            
            {/* Production Stats */}
            <div className="stats-grid">
              <div className="stat-card" style={{borderLeft: '4px solid #f59e0b'}}>
                <div className="stat-icon" style={{background: '#fef3c7'}}><Mic size={24} color="#f59e0b" /></div>
                <div className="stat-info">
                  <span className="stat-value">{productionStats?.awaiting_audio || 0}</span>
                  <span className="stat-label">Awaiting Audio</span>
                </div>
              </div>
              <div className="stat-card" style={{borderLeft: '4px solid #3b82f6'}}>
                <div className="stat-icon" style={{background: '#dbeafe'}}><Edit3 size={24} color="#3b82f6" /></div>
                <div className="stat-info">
                  <span className="stat-value">{productionStats?.audio_received || 0}</span>
                  <span className="stat-label">Editor&apos;s Desk</span>
                </div>
              </div>
              <div className="stat-card" style={{borderLeft: '4px solid #8b5cf6'}}>
                <div className="stat-icon" style={{background: '#ede9fe'}}><Printer size={24} color="#8b5cf6" /></div>
                <div className="stat-info">
                  <span className="stat-value">{productionStats?.ready_to_plot || 0}</span>
                  <span className="stat-label">Ready to Plot</span>
                </div>
              </div>
              <div className="stat-card" style={{borderLeft: '4px solid #10b981'}}>
                <div className="stat-icon" style={{background: '#d1fae5'}}><Package size={24} color="#10b981" /></div>
                <div className="stat-info">
                  <span className="stat-value">{productionStats?.dispatched || 0}</span>
                  <span className="stat-label">Dispatched</span>
                </div>
              </div>
            </div>
            
            {productionStats?.nudges_needed > 0 && (
              <div className="alert-banner" style={{background: '#fef2f2', border: '1px solid #fecaca', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <Bell size={20} color="#dc2626" />
                <span style={{color: '#dc2626', fontWeight: 500}}>{productionStats.nudges_needed} loved one(s) need a nudge SMS (22+ days since last call)</span>
              </div>
            )}

            {/* Editor's Desk */}
            <div className="production-section">
              <h2><Edit3 size={20} /> Editor&apos;s Desk</h2>
              <p className="section-desc">Transcribe recordings, review, and prepare for plotting</p>
              
              {editorsDesk.length === 0 ? (
                <div className="empty-state">No recordings waiting for review</div>
              ) : (
                <div className="production-cards">
                  {editorsDesk.map(sub => (
                    <div key={sub.id} className="production-card">
                      <div className="card-header">
                        <strong>{sub.parent_preferred_name || 'Loved One'}</strong>
                        <span className="subscriber">for {sub.subscriber_name || sub.user_email}</span>
                      </div>
                      <div className="card-month">{sub.current_month}</div>
                      
                      {/* Audio Player (proxied through backend) */}
                      {sub.current_audio_url ? (
                        <div className="audio-player" data-testid={`audio-player-${sub.id}`}>
                          <audio controls preload="none" style={{width:'100%', height:'32px'}}>
                            <source src={`${API_URL}/api/admin/voice/recording-proxy?url=${encodeURIComponent(sub.current_audio_url)}&token=${localStorage.getItem('bt_admin_token')}`} type="audio/mpeg" />
                          </audio>
                        </div>
                      ) : availableRecordings.length > 0 ? (
                        <div style={{margin:'8px 0'}}>
                          <label style={{fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:'4px'}}>No audio linked — select a recording:</label>
                          <select 
                            data-testid={`link-recording-${sub.id}`}
                            onChange={(e) => e.target.value && linkRecording(sub.id, e.target.value)}
                            style={{width:'100%', padding:'6px 8px', borderRadius:'6px', border:'1px solid #d1d5db', fontSize:'0.85rem'}}
                          >
                            <option value="">Choose recording...</option>
                            {availableRecordings.map(r => (
                              <option key={r.sid} value={r.url}>
                                {new Date(r.date_created).toLocaleDateString()} — {r.duration}s
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <p style={{fontSize:'0.8rem', color:'#9ca3af', margin:'8px 0'}}>No recordings available</p>
                      )}
                      
                      {/* Transcription Workflow */}
                      {editingSubscription === sub.id ? (
                        <div className="transcript-editor" style={{maxWidth:'none'}}>
                          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', alignItems:'start'}}>
                            {/* Left: Editor */}
                            <div>
                              <label style={{fontWeight:600, marginBottom:'4px', display:'block'}}>Edit Transcript:</label>
                              <textarea
                                value={transcriptText}
                                onChange={(e) => setTranscriptText(e.target.value)}
                                placeholder="Transcript will appear here after transcription..."
                                rows={18}
                                style={{fontFamily:'Georgia, serif', lineHeight:'1.8', width:'100%', padding:'12px', borderRadius:'8px', border:'1px solid #d1d5db', resize:'vertical'}}
                                data-testid={`transcript-editor-${sub.id}`}
                              />
                              <div className="editor-actions" style={{marginTop:'12px'}}>
                                <button onClick={() => saveTranscript(sub.id)} className="btn-save" disabled={savingTranscript}>
                                  <Save size={16} /> {savingTranscript ? 'Saving...' : 'Save Draft'}
                                </button>
                                <button onClick={() => exportPdf(sub.id)} className="btn-export" title="Download A5 PDF for home printer (top gap for stamp)" data-testid={`export-pdf-${sub.id}`}>
                                  <Printer size={16} /> Export PDF for Printer
                                </button>
                                <button onClick={() => exportSvg(sub.id)} className="btn-export" title="Download SVG for iDraw pen plotter" data-testid={`export-svg-${sub.id}`}>
                                  <FileText size={16} /> Export SVG for Plotter
                                </button>
                                <button onClick={() => approveTranscript(sub.id)} className="btn-approve">
                                  <Check size={16} /> Approve & Move to Plot
                                </button>
                                <button onClick={() => { setEditingSubscription(null); setTranscriptText(''); }} className="btn-cancel">
                                  Cancel
                                </button>
                              </div>
                            </div>
                            
                            {/* Right: Letter Preview */}
                            <LetterPreview 
                              text={transcriptText}
                              lovedOne={sub.parent_preferred_name || sub.parent_full_name || 'Your Loved One'}
                              subscriber={sub.subscriber_name || sub.user_email || 'You'}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="card-actions" style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                          {sub.current_audio_url && (
                            <button
                              onClick={() => transcribeRecording(sub.id, sub.current_audio_url)}
                              className="btn-transcribe"
                              disabled={transcribing === sub.id}
                              data-testid={`transcribe-${sub.id}`}
                            >
                              <Mic size={16} /> {transcribing === sub.id ? 'Transcribing...' : 'Transcribe with AI'}
                            </button>
                          )}
                          <button
                            onClick={() => loadTranscript(sub.id)}
                            className="btn-edit"
                            data-testid={`edit-transcript-${sub.id}`}
                          >
                            <Edit3 size={16} /> Edit Transcript
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Plotting Batch */}
            <div className="production-section">
              <h2><Printer size={20} /> Plotting Batch</h2>
              <p className="section-desc">Letters ready for the PlotBot</p>
              
              {plottingBatch.length === 0 ? (
                <div className="empty-state">No letters ready for plotting</div>
              ) : (
                <>
                  <div className="batch-actions">
                    <button 
                      onClick={() => bulkUpdateStatus(plottingBatch.map(s => s.id), 'dispatched')}
                      className="btn-batch"
                    >
                      <Package size={16} /> Mark All as Plotted
                    </button>
                  </div>
                  <div className="production-list">
                    {plottingBatch.map(sub => (
                      <div key={sub.id} className="production-list-item">
                        <span className="name">{sub.parent_preferred_name || 'Loved One'}</span>
                        <span className="subscriber">for {sub.subscriber_name || sub.user_email}</span>
                        <span className="month">{sub.current_month}</span>
                        <button onClick={() => bulkUpdateStatus([sub.id], 'audio_received')} className="btn-back-small" title="Move back to Editor's Desk">
                          <Edit3 size={14} /> Edit
                        </button>
                        <button onClick={() => exportSvg(sub.id, 1)} className="btn-export-small" title="Download Page 1">
                          <FileText size={14} /> P1
                        </button>
                        <button onClick={() => exportSvg(sub.id, 2)} className="btn-export-small" title="Download Page 2">
                          <FileText size={14} /> P2
                        </button>
                        <button onClick={() => exportSvg(sub.id, 3)} className="btn-export-small" title="Download Page 3">
                          <FileText size={14} /> P3
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Assembly Line */}
            <div className="production-section">
              <h2><Package size={20} /> Assembly Line</h2>
              <p className="section-desc">Ready to dispatch - will send email notification</p>
              
              {assemblyLine.length === 0 ? (
                <div className="empty-state">No letters waiting to dispatch</div>
              ) : (
                <>
                  <div className="batch-actions">
                    <button 
                      onClick={() => markDispatched(assemblyLine.map(s => s.id))}
                      className="btn-dispatch"
                    >
                      <Check size={16} /> Mark All Dispatched & Send Emails
                    </button>
                  </div>
                  <div className="production-list">
                    {assemblyLine.map(sub => (
                      <div key={sub.id} className="production-list-item">
                        <span className="name">{sub.parent_preferred_name || 'Loved One'}</span>
                        <span className="subscriber">for {sub.subscriber_name || sub.user_email}</span>
                        <span className="month">{sub.current_month}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="dashboard-content">
            <h1>Dashboard Overview</h1>
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon signups"><UserPlus size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.signups_today}</span>
                  <span className="stat-label">Signups Today</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon signups"><UserPlus size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.signups_week}</span>
                  <span className="stat-label">Signups This Week</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon signups"><UserPlus size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.signups_month}</span>
                  <span className="stat-label">Signups This Month</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon revenue"><DollarSign size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">AUD ${stats.revenue_today.toFixed(2)}</span>
                  <span className="stat-label">Revenue Today</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon revenue"><DollarSign size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">AUD ${stats.revenue_week.toFixed(2)}</span>
                  <span className="stat-label">Revenue This Week</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon revenue"><DollarSign size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">AUD ${stats.revenue_month.toFixed(2)}</span>
                  <span className="stat-label">Revenue This Month</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon active"><Activity size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.active_subscriptions}</span>
                  <span className="stat-label">Active Subscriptions</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon customers"><Users size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.total_customers}</span>
                  <span className="stat-label">Total Customers</span>
                </div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-icon earlybird"><AlertCircle size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.early_bird_remaining}</span>
                  <span className="stat-label">Early Bird Spots Left</span>
                </div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon pending"><AlertCircle size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{stats.pending_onboardings}</span>
                  <span className="stat-label">Pending Onboardings</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="customers-content">
            <h1>Customers</h1>
            
            {!selectedCustomer ? (
              <>
                <div className="search-bar">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && loadDashboardData()}
                  />
                  <button onClick={loadDashboardData}>Search</button>
                </div>
                
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Subscription</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map(customer => (
                        <tr key={customer.id}>
                          <td>{customer.email}</td>
                          <td>{customer.name || '-'}</td>
                          <td>
                            <span className={`badge ${customer.subscription?.status || 'none'}`}>
                              {customer.subscription?.status || 'No subscription'}
                            </span>
                          </td>
                          <td>{new Date(customer.created_at).toLocaleDateString()}</td>
                          <td>
                            <button 
                              className="btn-small"
                              onClick={() => loadCustomerDetail(customer.id)}
                            >
                              View <ChevronRight size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="table-footer">Showing {customers.length} of {customersTotal} customers</p>
                </div>
              </>
            ) : (
              <CustomerDetail 
                customer={selectedCustomer} 
                onBack={() => setSelectedCustomer(null)}
                onUpload={handleFileUpload}
              />
            )}
          </div>
        )}

        {/* Subscriptions Tab */}
        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="reports-content">
            <h1>Reports</h1>
            
            <div className="chart-section">
              <h2>Daily Signups (Last 30 Days)</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={signupData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="signups" stroke="#8b9a7d" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-section">
              <h2>Daily Revenue (Last 30 Days)</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                    <Bar dataKey="revenue" fill="#c4a4a4" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-section">
              <h2>Monthly Churn Rate</h2>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Active at Start</th>
                      <th>Cancelled</th>
                      <th>Churn Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {churnData.map(row => (
                      <tr key={row.month}>
                        <td>{row.month}</td>
                        <td>{row.active_start}</td>
                        <td>{row.cancelled}</td>
                        <td>{row.churn_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* SMS Tab */}
        {activeTab === 'sms' && (
          <div className="sms-content">
            <div className="sms-header">
              <h1>SMS Management</h1>
              <div className="sms-actions">
                <button 
                  className="btn-sms-action" 
                  onClick={runNudgeCheck}
                  disabled={actionLoading}
                  data-testid="run-nudge-check-btn"
                >
                  <RefreshCw size={16} /> Run Nudge Check
                </button>
              </div>
            </div>

            <div className="sms-info-card" data-testid="sms-info">
              <h3>How SMS Works</h3>
              <ul>
                <li><strong>Welcome SMS</strong> — Sent automatically when a subscriber completes onboarding. Can also be sent manually below.</li>
                <li><strong>Nudge SMS</strong> — Sent automatically after 21 days of no audio received. Runs daily, or you can trigger a check manually.</li>
              </ul>
            </div>

            <h2>Send SMS Manually</h2>
            <div className="sms-manual-grid">
              {subscriptions.filter(s => s.onboarding_complete && s.parent_phone).length === 0 ? (
                <p className="empty-state">No onboarded subscriptions with phone numbers found.</p>
              ) : (
                subscriptions.filter(s => s.onboarding_complete && s.parent_phone).map(sub => (
                  <div key={sub.id} className="sms-sub-card" data-testid={`sms-card-${sub.id}`}>
                    <div className="sms-sub-info">
                      <strong>{sub.parent_preferred_name || sub.parent_full_name}</strong>
                      <span>{sub.parent_phone}</span>
                      <span className="sms-sub-meta">Subscriber: {sub.subscriber_name || sub.user_email}</span>
                    </div>
                    <div className="sms-sub-actions">
                      <button 
                        className="btn-sms btn-welcome-sms"
                        onClick={() => sendWelcomeSms(sub.id)}
                        disabled={actionLoading}
                        data-testid={`send-welcome-${sub.id}`}
                      >
                        <Send size={14} /> Welcome
                      </button>
                      <button 
                        className="btn-sms btn-nudge-sms"
                        onClick={() => sendNudgeSms(sub.id)}
                        disabled={actionLoading}
                        data-testid={`send-nudge-${sub.id}`}
                      >
                        <Bell size={14} /> Nudge
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <h2>SMS Log</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Recipient</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Triggered By</th>
                    <th>Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {smsLogs.length === 0 ? (
                    <tr><td colSpan={6} className="empty-state">No SMS messages sent yet.</td></tr>
                  ) : (
                    smsLogs.map((log, i) => (
                      <tr key={i}>
                        <td><span className={`badge ${log.type}`}>{log.type}</span></td>
                        <td>{log.recipient_name}</td>
                        <td>{log.phone_number}</td>
                        <td><span className={`badge ${log.success ? 'active' : 'cancelled'}`}>{log.success ? 'Sent' : 'Failed'}</span></td>
                        <td>{log.triggered_by || 'auto'}</td>
                        <td>{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Refund Modal */}
        {refundModal && (
          <div className="admin-modal-overlay" data-testid="refund-modal">
            <div className="admin-modal">
              <div className="modal-header">
                <h3>Process Refund</h3>
                <button onClick={() => setRefundModal(null)} className="modal-close"><X size={20} /></button>
              </div>
              <div className="modal-body">
                <p className="modal-sub">Subscription: <strong>{refundModal.subscriber_name || refundModal.user_email}</strong></p>
                <p className="modal-sub">Product: <strong>{refundModal.product_id}</strong></p>
                <div className="modal-field">
                  <label>Refund Type</label>
                  <select value={refundType} onChange={(e) => setRefundType(e.target.value)} data-testid="refund-type-select">
                    <option value="full">Full Refund</option>
                    <option value="partial">Partial Refund</option>
                  </select>
                </div>
                {refundType === 'partial' && (
                  <div className="modal-field">
                    <label>Amount ($)</label>
                    <input type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder="e.g., 29.00" step="0.01" min="0.01" data-testid="refund-amount-input" />
                  </div>
                )}
                <div className="modal-field">
                  <label>Reason (optional)</label>
                  <textarea value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="e.g., Loved one opted out within 14 days" rows={3} data-testid="refund-reason-input" />
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={() => setRefundModal(null)} className="btn-modal-cancel">Cancel</button>
                <button onClick={handleRefund} className="btn-modal-confirm btn-danger"
                  disabled={actionLoading || (refundType === 'partial' && !refundAmount)} data-testid="confirm-refund-btn">
                  {actionLoading ? 'Processing...' : 'Confirm Refund'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pause Modal */}
        {pauseModal && (
          <div className="admin-modal-overlay" data-testid="pause-modal">
            <div className="admin-modal">
              <div className="modal-header">
                <h3>Pause Subscription</h3>
                <button onClick={() => setPauseModal(null)} className="modal-close"><X size={20} /></button>
              </div>
              <div className="modal-body">
                <p className="modal-sub">Subscription: <strong>{pauseModal.subscriber_name || pauseModal.user_email}</strong></p>
                <p className="modal-info">Pausing will hold billing for the selected period. The customer stays in your system and can be resumed at any time.</p>
                <div className="modal-field">
                  <label>Pause Duration</label>
                  <select value={pauseMonths} onChange={(e) => setPauseMonths(parseInt(e.target.value))} data-testid="pause-months-select">
                    <option value={1}>1 Month</option>
                    <option value={2}>2 Months</option>
                    <option value={3}>3 Months</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={() => setPauseModal(null)} className="btn-modal-cancel">Cancel</button>
                <button onClick={handlePause} className="btn-modal-confirm"
                  disabled={actionLoading} data-testid="confirm-pause-btn">
                  {actionLoading ? 'Processing...' : 'Confirm Pause'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activity Feed Tab */}
        {activeTab === 'activity' && (
          <div className="activity-feed-content" data-testid="activity-feed">
            <h1>Activity Feed</h1>
            <p style={{color:'#6b7280', marginBottom:'1.5rem'}}>
              Real-time log of all customer actions — {activityTotal} total events
            </p>
            <div style={{display:'flex', flexDirection:'column', gap:'0'}}>
              {activityFeed.length === 0 ? (
                <p style={{textAlign:'center', color:'#9ca3af', padding:'2rem'}}>No activity recorded yet.</p>
              ) : (
                activityFeed.map((act, i) => {
                  const typeConfig = {
                    account_created: { icon: <UserPlus size={16} />, color: '#10b981', label: 'Account Created' },
                    purchase: { icon: <ShoppingBag size={16} />, color: '#8b5cf6', label: 'Purchase' },
                    onboarding_completed: { icon: <Check size={16} />, color: '#3b82f6', label: 'Onboarding Complete' },
                    call_received: { icon: <Phone size={16} />, color: '#f59e0b', label: 'Call Received' },
                    transcript_created: { icon: <FileText size={16} />, color: '#6366f1', label: 'Transcript Created' },
                    letter_exported: { icon: <Printer size={16} />, color: '#ec4899', label: 'Letter Exported' },
                    refund_processed: { icon: <DollarSign size={16} />, color: '#ef4444', label: 'Refund Processed' },
                    subscription_paused: { icon: <PauseCircle size={16} />, color: '#f97316', label: 'Subscription Paused' },
                    subscription_resumed: { icon: <Play size={16} />, color: '#22c55e', label: 'Subscription Resumed' },
                  };
                  const cfg = typeConfig[act.type] || { icon: <Activity size={16} />, color: '#6b7280', label: act.type };
                  const timeAgo = (() => {
                    const diff = Date.now() - new Date(act.created_at).getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 1) return 'just now';
                    if (mins < 60) return `${mins}m ago`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h ago`;
                    const days = Math.floor(hrs / 24);
                    return `${days}d ago`;
                  })();

                  return (
                    <div key={act.id || i} data-testid={`activity-item-${i}`} style={{
                      display:'flex', alignItems:'flex-start', gap:'12px',
                      padding:'14px 16px', borderBottom:'1px solid #f3f4f6',
                      background: i % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}>
                      <div style={{
                        width:'32px', height:'32px', borderRadius:'50%',
                        background: `${cfg.color}18`, color: cfg.color,
                        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
                      }}>
                        {cfg.icon}
                      </div>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px'}}>
                          <span style={{fontWeight:600, fontSize:'0.9rem', color:'#1f2937'}}>{cfg.label}</span>
                          <span style={{fontSize:'0.78rem', color:'#9ca3af', whiteSpace:'nowrap'}}>{timeAgo}</span>
                        </div>
                        <div style={{fontSize:'0.85rem', color:'#6b7280', marginTop:'2px'}}>
                          {act.user_name || act.user_email || act.user_id?.slice(0,8) || 'Unknown'}
                          {act.details?.product_id && <span> &mdash; {act.details.product_id}</span>}
                          {act.details?.amount && <span> &mdash; ${act.details.amount?.toFixed?.(2) || act.details.amount} {act.details.currency?.toUpperCase?.() || ''}</span>}
                          {act.details?.loved_one_name && <span> &mdash; {act.details.loved_one_name}</span>}
                          {act.details?.duration && <span> &mdash; {act.details.duration}s call</span>}
                          {act.details?.subscription_id && <span style={{fontSize:'0.75rem', color:'#9ca3af'}}> (sub: {act.details.subscription_id.slice(0,8)})</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="leads-content" data-testid="leads-tab-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h1>Email Leads <span style={{ fontSize: '1rem', fontWeight: 400, color: '#888' }}>({leadsTotal})</span></h1>
              {leads.length > 0 && (
                <button
                  className="btn btn-primary"
                  data-testid="export-leads-btn"
                  onClick={() => {
                    const csv = 'Name,Email,Source,Date\n' + leads.map(l => 
                      `${l.name || ''},${l.email},${l.source},${l.created_at}`
                    ).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `binding-thread-leads-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('CSV exported');
                  }}
                >
                  <Download size={16} /> Export CSV
                </button>
              )}
            </div>

            {leads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                <Mail size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                <p>No leads yet. Once visitors download the free guide, their emails will appear here.</p>
              </div>
            ) : (
              <table className="admin-table" data-testid="leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Source</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{lead.name || '-'}</td>
                      <td>{lead.email}</td>
                      <td>
                        <span style={{ 
                          background: '#e8f5e9', 
                          color: '#2e7d32', 
                          padding: '0.2rem 0.6rem', 
                          borderRadius: '12px', 
                          fontSize: '0.8rem' 
                        }}>
                          {lead.source}
                        </span>
                      </td>
                      <td style={{ color: '#888' }}>
                        {new Date(lead.created_at).toLocaleDateString('en-AU', { 
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <div className="newsletter-content" data-testid="newsletter-tab-content" style={{ maxWidth: '780px' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h1 style={{ margin: 0 }}>Newsletter</h1>
              <p style={{ color: '#888', margin: '0.4rem 0 0' }}>
                Write a branded broadcast to your active leads.
                {newsletterPreview && (
                  <span style={{ marginLeft: '0.5rem', color: '#5c4a3d' }}>
                    <strong>{newsletterPreview.active_recipients}</strong> active
                    {newsletterPreview.unsubscribed > 0 && (
                      <span style={{ color: '#aaa' }}> · {newsletterPreview.unsubscribed} unsubscribed</span>
                    )}
                  </span>
                )}
              </p>
            </div>

            <div style={{ background: '#fff', border: '1px solid rgba(139,154,125,0.25)', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5c4a3d', marginBottom: '0.4rem' }}>
                Subject line
              </label>
              <input
                type="text"
                value={newsletterSubject}
                onChange={(e) => setNewsletterSubject(e.target.value)}
                placeholder="e.g. A small story this week — and a question for you"
                data-testid="newsletter-subject-input"
                style={{ width: '100%', padding: '0.7rem 0.9rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', marginBottom: '1.25rem', fontFamily: 'inherit' }}
              />

              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5c4a3d', marginBottom: '0.4rem' }}>
                Message <span style={{ color: '#aaa', fontWeight: 400 }}>(plain text — line breaks become paragraphs)</span>
              </label>
              <textarea
                value={newsletterBody}
                onChange={(e) => setNewsletterBody(e.target.value)}
                placeholder={"Write your newsletter here…\n\nEach blank line creates a new paragraph."}
                rows={14}
                data-testid="newsletter-body-input"
                style={{ width: '100%', padding: '0.85rem 0.95rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', fontFamily: 'Georgia, serif', lineHeight: 1.6, resize: 'vertical' }}
              />

              <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: '#888', fontStyle: 'italic' }}>
                Recipients are greeted with "Hi [first name]," automatically. A branded header and an unsubscribe link in the footer are added when sent.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '2rem' }}>
              <input
                type="email"
                value={newsletterTestEmail}
                onChange={(e) => setNewsletterTestEmail(e.target.value)}
                placeholder="Test email"
                data-testid="newsletter-test-email-input"
                style={{ flex: '1 1 240px', padding: '0.65rem 0.85rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
              />
              <button
                className="btn"
                disabled={newsletterSending || !newsletterSubject.trim() || newsletterBody.trim().length < 10 || !newsletterTestEmail.trim()}
                data-testid="newsletter-send-test-btn"
                onClick={async () => {
                  setNewsletterSending(true);
                  try {
                    await axios.post(
                      `${API_URL}/api/admin/newsletter/send-test`,
                      { subject: newsletterSubject, body: newsletterBody, test_email: newsletterTestEmail },
                      authHeaders
                    );
                    toast.success(`Test sent to ${newsletterTestEmail}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to send test');
                  } finally {
                    setNewsletterSending(false);
                  }
                }}
                style={{ background: '#fff', border: '1px solid #8b9a7d', color: '#5c4a3d', padding: '0.65rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                {newsletterSending ? 'Sending…' : 'Send test'}
              </button>
              <button
                className="btn btn-primary"
                disabled={newsletterSending || !newsletterSubject.trim() || newsletterBody.trim().length < 10 || !newsletterPreview || newsletterPreview.active_recipients === 0}
                data-testid="newsletter-send-all-btn"
                onClick={() => setNewsletterConfirmOpen(true)}
                style={{ background: '#8b9a7d', color: '#fff', border: 'none', padding: '0.65rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                <Send size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                Send to all leads
              </button>
            </div>

            {/* Confirm modal */}
            {newsletterConfirmOpen && (
              <div
                onClick={() => !newsletterSending && setNewsletterConfirmOpen(false)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                data-testid="newsletter-confirm-modal"
              >
                <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '2rem', borderRadius: '10px', maxWidth: '440px', width: '90%' }}>
                  <h2 style={{ marginTop: 0, color: '#5c4a3d' }}>Send to all leads?</h2>
                  <p style={{ color: '#5c4a3d', lineHeight: 1.6 }}>
                    This will deliver "<strong>{newsletterSubject}</strong>" to{' '}
                    <strong>{newsletterPreview?.active_recipients || 0}</strong> active subscribers.
                    This action cannot be undone.
                  </p>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                    <button
                      onClick={() => setNewsletterConfirmOpen(false)}
                      disabled={newsletterSending}
                      style={{ padding: '0.6rem 1.1rem', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}
                      data-testid="newsletter-cancel-btn"
                    >Cancel</button>
                    <button
                      onClick={async () => {
                        setNewsletterSending(true);
                        try {
                          const r = await axios.post(
                            `${API_URL}/api/admin/newsletter/send`,
                            { subject: newsletterSubject, body: newsletterBody },
                            authHeaders
                          );
                          toast.success(`Queued ${r.data.recipients} recipients`);
                          setNewsletterSubject('');
                          setNewsletterBody('');
                          setNewsletterConfirmOpen(false);
                          // Refresh history shortly
                          setTimeout(async () => {
                            const h = await axios.get(`${API_URL}/api/admin/newsletter/history`, authHeaders);
                            setNewsletterHistory(h.data.sends || []);
                          }, 1500);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to send');
                        } finally {
                          setNewsletterSending(false);
                        }
                      }}
                      disabled={newsletterSending}
                      style={{ padding: '0.6rem 1.1rem', background: '#8b9a7d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      data-testid="newsletter-confirm-send-btn"
                    >{newsletterSending ? 'Sending…' : 'Yes, send now'}</button>
                  </div>
                </div>
              </div>
            )}

            {/* History */}
            <div>
              <h2 style={{ fontSize: '1.1rem', color: '#5c4a3d', margin: '0 0 0.75rem' }}>Recent sends</h2>
              {newsletterHistory.length === 0 ? (
                <p style={{ color: '#aaa', fontStyle: 'italic' }}>No newsletters sent yet.</p>
              ) : (
                <table className="admin-table" data-testid="newsletter-history-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Recipients</th>
                      <th>Delivered</th>
                      <th>Status</th>
                      <th>Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsletterHistory.map((s) => (
                      <tr key={s.send_id}>
                        <td style={{ fontWeight: 500 }}>{s.subject}</td>
                        <td>{s.recipients_count}</td>
                        <td>
                          {s.delivered}{s.failed > 0 && <span style={{ color: '#c0392b' }}> · {s.failed} failed</span>}
                        </td>
                        <td>
                          <span style={{
                            background: s.status === 'completed' ? '#e8f5e9' : '#fff3cd',
                            color: s.status === 'completed' ? '#2e7d32' : '#856404',
                            padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.78rem'
                          }}>
                            {s.status}
                          </span>
                        </td>
                        <td style={{ color: '#888' }}>
                          {new Date(s.sent_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Signups Tab (Legacy Orders + Loved One Onboarding) */}
        {activeTab === 'legacy' && (
          <div className="legacy-content" data-testid="legacy-tab-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
              <div>
                <h1 style={{ margin: 0 }}>Signups</h1>
                <p style={{ color: '#888', margin: '0.4rem 0 1.5rem' }}>
                  Each row is one customer signup. Click a row to see who they bought it for, their addresses, and the stage they&apos;re at.
                </p>
              </div>
              <button
                className="btn btn-secondary"
                onClick={runLegacyNudgeCheck}
                data-testid="legacy-run-nudge-btn"
                style={{ marginTop: 8 }}
              >
                <Send size={16} /> Send pending nudges
              </button>
            </div>

            {legacySummary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <div className="stat-card" style={{ padding: '1rem', background: '#f8f6f1', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '.08em' }}>Total Signups</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#3a3530', marginTop: 4 }} data-testid="legacy-summary-total">
                    {legacySummary.total}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: '1rem', background: '#fff7e6', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '.08em' }}>Awaiting Onboarding</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#c9a96e', marginTop: 4 }}>
                    {legacySummary.awaiting_onboarding ?? 0}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: '1rem', background: '#f0f4ee', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '.08em' }}>Onboarded</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#4a5b42', marginTop: 4 }}>
                    {legacySummary.onboarded ?? 0}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: '1rem', background: '#fff7e6', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '.08em' }}>Letters Pending</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#c9a96e', marginTop: 4 }}>
                    {legacySummary.letters_pending}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: '1rem', background: '#f0f4ee', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '.08em' }}>Letters Delivered</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#4a5b42', marginTop: 4 }}>
                    {legacySummary.letters_delivered_total}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: '1rem', background: '#f8f6f1', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '.08em' }}>Completed</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#3a3530', marginTop: 4 }}>
                    {legacySummary.completed}
                  </div>
                </div>
              </div>
            )}

            {legacyOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                <Package size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                <p>No signups yet. New customers will appear here after checkout.</p>
              </div>
            ) : (
              <table className="admin-table" data-testid="legacy-orders-table">
                <thead>
                  <tr>
                    <th style={{ width: 26 }}></th>
                    <th>Customer</th>
                    <th>Loved One</th>
                    <th>Collection</th>
                    <th>Onboarding</th>
                    <th>Letters</th>
                    <th>Purchased</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {legacyOrders.map((o) => {
                    const isOpen = expandedSignupId === o.id;
                    const pct = o.letters_included > 0
                      ? Math.round((o.letters_delivered / o.letters_included) * 100)
                      : 0;
                    const lovedDisplay = o.loved_one_preferred_name || o.loved_one_full_name || '';
                    const onboarded = !!o.onboarding_complete;
                    return (
                      <React.Fragment key={o.id}>
                        <tr
                          data-testid={`legacy-order-row-${o.id}`}
                          onClick={() => setExpandedSignupId(isOpen ? null : o.id)}
                          style={{ cursor: 'pointer', background: isOpen ? '#fafaf6' : 'transparent' }}
                        >
                          <td style={{ color: '#888' }}>{isOpen ? '\u25BE' : '\u25B8'}</td>
                          <td style={{ fontWeight: 500 }}>
                            <div>{o.customer_full_name || <em style={{ color: '#aaa' }}>not yet provided</em>}</div>
                            <div style={{ fontSize: 12, color: '#888' }}>{o.user_email || '\u2014'}</div>
                          </td>
                          <td>
                            {lovedDisplay
                              ? <div>{lovedDisplay}{o.relationship && <div style={{ fontSize: 12, color: '#888' }}>({o.relationship})</div>}</div>
                              : <em style={{ color: '#aaa' }}>awaiting onboarding</em>}
                          </td>
                          <td>{o.collection_name}</td>
                          <td>
                            <span style={{
                              background: onboarded ? '#e8f5e9' : '#fff7e6',
                              color: onboarded ? '#2e7d32' : '#8a6914',
                              padding: '0.2rem 0.6rem',
                              borderRadius: 12,
                              fontSize: '0.78rem'
                            }}>
                              {onboarded ? 'Complete' : 'Pending'}
                            </span>
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 5, background: '#eee', borderRadius: 4, overflow: 'hidden', minWidth: 70 }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: '#4a5b42' }} />
                              </div>
                              <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                                {o.letters_delivered}/{o.letters_included}
                              </span>
                            </div>
                          </td>
                          <td style={{ color: '#888', fontSize: '0.83rem' }}>
                            {o.created_at ? new Date(o.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '\u2014'}
                          </td>
                          <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            {o.letters_remaining > 0 && (
                              <button
                                className="btn btn-primary"
                                style={{ padding: '6px 12px', fontSize: '0.78rem' }}
                                onClick={() => markLegacyLetterDelivered(o.id)}
                                data-testid={`legacy-mark-delivered-${o.id}`}
                              >
                                Mark delivered
                              </button>
                            )}
                            {o.letters_delivered > 0 && (
                              <button
                                style={{ padding: '6px 10px', fontSize: '0.78rem', marginLeft: 6, background: 'transparent', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                                onClick={() => undoLegacyLetter(o.id)}
                                data-testid={`legacy-undo-${o.id}`}
                                title="Undo most recent delivery"
                              >
                                Undo
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr data-testid={`legacy-signup-detail-${o.id}`}>
                            <td colSpan={8} style={{ background: '#fafaf6', padding: '1.25rem 1.5rem', borderBottom: '1px solid #eee' }}>
                              <SignupDetailPanel order={o} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-content">
            <h1>Settings</h1>
            
            <div className="settings-card">
              <h3>Early Bird Counter</h3>
              <p>Manually adjust the number of Early Bird spots remaining.</p>
              <div className="setting-row">
                <input
                  type="number"
                  value={earlyBirdCount}
                  onChange={(e) => setEarlyBirdCount(parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
                <button onClick={updateEarlyBirdCounter}>Update</button>
              </div>
            </div>

            <DeploymentHealthCheck token={token} />
          </div>
        )}

        {/* Plotter Guide Tab */}
        {activeTab === 'help' && (
          <div className="settings-content" data-testid="plotter-guide">
            <h1>Production Guide</h1>
            <p style={{color: 'var(--text-secondary)', marginBottom: '1.5rem'}}>How to produce letters with the home printer (current workflow) and the iDraw plotter (kept for future use).</p>

            <div className="settings-card" style={{borderLeft: '4px solid #8b9a7d'}}>
              <h3>Current Workflow: Home Printer + Stamped Logo</h3>
              <ol className="plotter-steps">
                <li><strong>Production Hub</strong> &mdash; find the subscription in Editor&apos;s Desk</li>
                <li><strong>Edit Transcript</strong> &mdash; review the text and edit anywhere it needs adjusting, then <strong>Save Draft</strong></li>
                <li><strong>Export PDF for Printer</strong> &mdash; downloads an A5 PDF with a 40mm top gap left for your stamp</li>
                <li><strong>Stamp the Binding Thread logo</strong> on the top of an A5 sheet of archival paper (stamp area: 63 &times; 32 mm)</li>
                <li><strong>Load the stamped sheet</strong> into your home printer (A5 portrait)</li>
                <li><strong>Open the PDF</strong> and print &mdash; the letter will land cleanly below your stamp</li>
                <li><strong>Fold the A5 letter once</strong> and place into a C6 envelope</li>
                <li>Back in the admin, click <strong>Approve</strong> to move the letter through the rest of the production pipeline</li>
              </ol>
            </div>

            <div className="settings-card" style={{opacity: 0.85}}>
              <h3>Optional: Plotter Workflow (kept for future)</h3>
              <ol className="plotter-steps">
                <li><strong>Export SVG for Plotter</strong> on the Editor&apos;s Desk card</li>
                <li><strong>Open Inkscape</strong> &rarr; File &rarr; Open the downloaded SVG</li>
                <li><strong>Ctrl+A</strong> to select everything</li>
                <li><strong>Extensions &rarr; Text &rarr; Hershey Text &rarr; Apply</strong> &mdash; converts to single-line plotter font</li>
                <li><strong>Load A5 parchment paper</strong> into the iDraw plotter (portrait)</li>
                <li><strong>Extensions &rarr; iDraw 2.0 Control &rarr; Plot &rarr; Apply</strong></li>
                <li>Wait for the plotter to finish, then fold and post</li>
              </ol>
            </div>

            <div className="settings-card">
              <h3>Tips &amp; Troubleshooting</h3>
              <ul className="plotter-steps">
                <li><strong>Re-edit a letter:</strong> Click the <strong>Edit</strong> button on the card in Plotting Batch to move it back to Editor&apos;s Desk</li>
                <li><strong>Text starting off the page:</strong> Adjust the paper position on the plotter bed, or the left margin can be adjusted in the code</li>
                <li><strong>Pen height:</strong> Extensions &rarr; iDraw 2.0 Control &rarr; Setup tab to adjust pen up/down heights</li>
                <li><strong>Font:</strong> Letters use Dancing Script (cursive). Text to paths conversion is required before plotting</li>
                <li><strong>Paper size:</strong> A5 portrait (148mm &times; 210mm), folds to fit C6 envelope</li>
                <li><strong>Multi-page letters:</strong> If text is too long, the SVG will show &quot;[continued on next page...]&quot; &mdash; export again for page 2</li>
              </ul>
            </div>

            <div className="settings-card">
              <h3>Letter Format</h3>
              <div style={{background: '#faf8f5', padding: '1rem', borderRadius: '8px', fontFamily: 'serif', fontSize: '0.9rem', lineHeight: '1.8'}}>
                <div style={{color: '#666', marginBottom: '0.75rem'}}>Thread No. 001. &nbsp;&nbsp;&nbsp; Recorded by: [Full Name]. &nbsp;&nbsp;&nbsp; Date: [Date]</div>
                <div style={{fontFamily: 'cursive', fontSize: '1rem'}}>
                  [Memory text in Dancing Script cursive]
                </div>
                <div style={{fontFamily: 'cursive', fontSize: '1rem', textAlign: 'right', marginTop: '1rem'}}>
                  [Preferred Name]
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Customer Detail Component - 360 View
function CustomerDetail({ customer, onBack, onUpload }) {
  const [uploadMonth, setUploadMonth] = useState('');
  const [uploadType, setUploadType] = useState('mp3');
  const [uploadFile, setUploadFile] = useState(null);
  const [sendNotification, setSendNotification] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  const handleUpload = () => {
    if (!uploadFile || !uploadMonth) {
      toast.error('Please select a file and month');
      return;
    }
    onUpload(customer.customer.id, uploadFile, uploadType, uploadMonth, sendNotification);
    setUploadFile(null);
    setUploadMonth('');
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'account_created': return <UserPlus size={14} />;
      case 'purchase': return <ShoppingBag size={14} />;
      case 'onboarding_completed': return <Check size={14} />;
      case 'call_received': return <Phone size={14} />;
      case 'subscription_paused': return <PauseCircle size={14} />;
      case 'subscription_resumed': return <RefreshCw size={14} />;
      case 'refund_processed': return <RotateCcw size={14} />;
      case 'email_sent': return <Mail size={14} />;
      case 'sms_sent': return <MessageSquare size={14} />;
      default: return <Activity size={14} />;
    }
  };

  const getActivityColor = (type) => {
    switch(type) {
      case 'account_created': return '#6B8E6B';
      case 'purchase': return '#C4A088';
      case 'onboarding_completed': return '#6B8E6B';
      case 'call_received': return '#3b82f6';
      case 'subscription_paused': return '#f59e0b';
      case 'subscription_resumed': return '#6B8E6B';
      case 'refund_processed': return '#ef4444';
      default: return '#6B7280';
    }
  };

  const getActivityLabel = (type) => {
    switch(type) {
      case 'account_created': return 'Account Created';
      case 'purchase': return 'Purchase Made';
      case 'onboarding_completed': return 'Onboarding Completed';
      case 'call_received': return 'Call Received';
      case 'subscription_paused': return 'Subscription Paused';
      case 'subscription_resumed': return 'Subscription Resumed';
      case 'refund_processed': return 'Refund Processed';
      case 'email_sent': return 'Email Sent';
      case 'sms_sent': return 'SMS Sent';
      default: return type?.replace(/_/g, ' ') || 'Activity';
    }
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: <Users size={14} /> },
    { id: 'letters', label: 'Letters', icon: <FileText size={14} /> },
    { id: 'timeline', label: 'Timeline', icon: <Clock size={14} /> },
    { id: 'recordings', label: 'Recordings', icon: <Phone size={14} /> },
    { id: 'orders', label: 'Orders & Refunds', icon: <CreditCard size={14} /> },
    { id: 'comms', label: 'Communications', icon: <Mail size={14} /> },
    { id: 'files', label: 'Files', icon: <FileText size={14} /> },
  ];

  return (
    <div className="customer-detail" data-testid="customer-360-view">
      <button className="back-btn" onClick={onBack} data-testid="back-to-customers">
        <ArrowLeft size={16} /> Back to Customers
      </button>

      <div className="detail-header">
        <div>
          <h2>{customer.subscription?.subscriber_name || customer.customer.name || customer.customer.email}</h2>
          <p style={{color: '#888', margin: '4px 0 0'}}>{customer.customer.email}</p>
        </div>
        <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
          <span className={`badge ${customer.subscription?.status || 'none'}`}>
            {customer.subscription?.status || 'No subscription'}
          </span>
          {customer.subscription?.is_founding && (
            <span className="badge founding">Founding Member</span>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div className="detail-section-tabs" data-testid="customer-section-tabs">
        {sections.map(sec => (
          <button
            key={sec.id}
            className={`section-tab ${activeSection === sec.id ? 'active' : ''}`}
            onClick={() => setActiveSection(sec.id)}
            data-testid={`section-${sec.id}`}
          >
            {sec.icon} {sec.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeSection === 'overview' && (
        <div className="detail-grid">
          <div className="detail-card">
            <h3>Subscriber Info (Gift Giver)</h3>
            <p><strong>Name:</strong> {customer.subscription?.subscriber_name || customer.customer.name || '-'}</p>
            <p><strong>Email:</strong> {customer.subscription?.subscriber_email || customer.customer.email}</p>
            <p><strong>Phone:</strong> {customer.subscription?.subscriber_phone || '-'}</p>
            <p><strong>Joined:</strong> {new Date(customer.customer.created_at).toLocaleDateString('en-AU', {dateStyle: 'long'})}</p>
            <p><strong>Founding Member:</strong> {customer.subscription?.is_founding ? 'Yes' : 'No'}</p>
            <p><strong>Product:</strong> {customer.subscription?.product_id || '-'}</p>
            <p><strong>Status:</strong> <span className={`badge ${customer.subscription?.status || 'none'}`}>{customer.subscription?.status || 'None'}</span></p>
            {customer.subscription?.paused_at && (
              <p><strong>Paused:</strong> {new Date(customer.subscription.paused_at).toLocaleDateString('en-AU')}</p>
            )}
            {customer.subscription?.expected_resume_date && (
              <p><strong>Resumes:</strong> {new Date(customer.subscription.expected_resume_date).toLocaleDateString('en-AU')}</p>
            )}
            <hr style={{margin:'12px 0', border:'none', borderTop:'1px solid #e5e1db'}} />
            <h4 style={{color:'#5c4a3d', marginBottom:'8px'}}>Shipping Address</h4>
            <p className="address">
              {customer.subscription?.shipping_address || '-'}<br />
              {customer.subscription?.shipping_city}{customer.subscription?.shipping_state ? `, ${customer.subscription.shipping_state}` : ''} {customer.subscription?.shipping_zip}<br />
              {customer.subscription?.shipping_country || 'AUS'}
            </p>
          </div>

          {customer.subscription && (
            <div className="detail-card">
              <h3>Loved One Details</h3>
              <p><strong>Full Name:</strong> {customer.subscription.parent_full_name || '-'}</p>
              <p><strong>Preferred Name:</strong> {customer.subscription.parent_preferred_name || '-'}</p>
              <p><strong>Phone:</strong> {customer.subscription.parent_phone || '-'}</p>
              <hr style={{margin:'12px 0', border:'none', borderTop:'1px solid #e5e1db'}} />
              <h4 style={{color:'#5c4a3d', marginBottom:'8px'}}>About Them</h4>
              <p><strong>Language/Accent:</strong> {customer.subscription.primary_language || '-'}</p>
              <p><strong>Sensitive Topics:</strong> {customer.subscription.sensitive_topics || '-'}</p>
              <p><strong>Special Interests:</strong> {customer.subscription.special_interests || '-'}</p>
            </div>
          )}

          {/* Call Recordings - prominent placement */}
          <div className="detail-card full-width" data-testid="overview-recordings">
            <h3><Phone size={16} style={{marginRight:'6px', verticalAlign:'middle'}} />Call Recordings</h3>
            {(!customer.call_logs || customer.call_logs.length === 0) ? (
              <p className="no-data">No calls recorded yet</p>
            ) : (
              <div className="table-container">
                <table className="compact-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Caller</th>
                      <th>Duration</th>
                      <th>Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.call_logs.map((log, i) => (
                      <tr key={log.id || i}>
                        <td>{log.created_at ? new Date(log.created_at).toLocaleString('en-AU', {dateStyle:'medium', timeStyle:'short'}) : '-'}</td>
                        <td>{log.caller_number || '-'}</td>
                        <td>{log.recording_duration ? `${log.recording_duration}s` : '-'}</td>
                        <td>
                          {log.recording_url ? (
                            <audio controls preload="none" style={{height:'28px', maxWidth:'280px'}}>
                              <source src={`${API_URL}/api/admin/voice/recording-proxy?url=${encodeURIComponent(log.recording_url)}&token=${localStorage.getItem('bt_admin_token')}`} type="audio/mpeg" />
                            </audio>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LETTERS ARCHIVE */}
      {activeSection === 'letters' && (
        <div className="letters-section" data-testid="customer-letters">
          <h3 style={{marginBottom:'16px', color:'#5c4a3d'}}>
            <FileText size={18} style={{marginRight:'8px', verticalAlign:'middle'}} />
            Letter History
          </h3>
          {(!customer.letters || customer.letters.length === 0) ? (
            <div style={{textAlign:'center', padding:'3rem 1rem', background:'#faf8f5', borderRadius:'12px', border:'1px solid #e5e1db'}}>
              <FileText size={40} style={{color:'#c4a088', marginBottom:'12px'}} />
              <p style={{color:'#6b7280', fontSize:'1rem'}}>No letters dispatched yet.</p>
              <p style={{color:'#9ca3af', fontSize:'0.85rem', marginTop:'4px'}}>Letters will appear here once they've been processed and mailed.</p>
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
              {customer.letters.map((letter, i) => (
                <div key={letter.id || i} data-testid={`letter-${i}`} style={{
                  background:'#fff', border:'1px solid #e5e1db', borderRadius:'12px',
                  padding:'20px', position:'relative'
                }}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px'}}>
                    <div>
                      <h4 style={{margin:0, color:'#5c4a3d', fontSize:'1.1rem'}}>{letter.month}</h4>
                      <p style={{margin:'4px 0 0', color:'#9ca3af', fontSize:'0.8rem'}}>
                        Dispatched {letter.dispatched_at ? new Date(letter.dispatched_at).toLocaleDateString('en-AU', {dateStyle:'long'}) : '-'}
                      </p>
                    </div>
                    <span style={{
                      background:'#6B8E6B18', color:'#6B8E6B', padding:'4px 12px',
                      borderRadius:'20px', fontSize:'0.78rem', fontWeight:600
                    }}>
                      Sent
                    </span>
                  </div>
                  <div style={{display:'flex', gap:'16px', flexWrap:'wrap'}}>
                    <div style={{flex:1, minWidth:'200px'}}>
                      <p style={{margin:'0 0 4px', fontSize:'0.8rem', color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px'}}>From</p>
                      <p style={{margin:0, color:'#5c4a3d'}}>{letter.loved_one_name || '-'}</p>
                    </div>
                    <div style={{flex:1, minWidth:'200px'}}>
                      <p style={{margin:'0 0 4px', fontSize:'0.8rem', color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px'}}>To</p>
                      <p style={{margin:0, color:'#5c4a3d'}}>{letter.subscriber_name || '-'}</p>
                    </div>
                  </div>
                  {letter.human_edited_transcript && (
                    <details style={{marginTop:'12px'}}>
                      <summary style={{cursor:'pointer', color:'#8b9a7d', fontWeight:600, fontSize:'0.9rem'}}>
                        View Transcript
                      </summary>
                      <div style={{
                        marginTop:'8px', padding:'14px', background:'#faf8f5',
                        borderRadius:'8px', fontSize:'0.9rem', lineHeight:1.7,
                        color:'#5c4a3d', whiteSpace:'pre-wrap', maxHeight:'300px', overflowY:'auto'
                      }}>
                        {letter.human_edited_transcript}
                      </div>
                    </details>
                  )}
                  {!letter.human_edited_transcript && letter.ai_transcript && (
                    <details style={{marginTop:'12px'}}>
                      <summary style={{cursor:'pointer', color:'#8b9a7d', fontWeight:600, fontSize:'0.9rem'}}>
                        View AI Transcript
                      </summary>
                      <div style={{
                        marginTop:'8px', padding:'14px', background:'#faf8f5',
                        borderRadius:'8px', fontSize:'0.9rem', lineHeight:1.7,
                        color:'#5c4a3d', whiteSpace:'pre-wrap', maxHeight:'300px', overflowY:'auto'
                      }}>
                        {letter.ai_transcript}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TIMELINE */}
      {activeSection === 'timeline' && (
        <div className="timeline-section" data-testid="customer-timeline">
          {(!customer.activities || customer.activities.length === 0) ? (
            <p className="no-data">No activity recorded yet</p>
          ) : (
            <div className="timeline">
              {customer.activities.map((activity, i) => (
                <div key={activity.id || i} className="timeline-item">
                  <div className="timeline-dot" style={{backgroundColor: getActivityColor(activity.type)}}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <strong>{getActivityLabel(activity.type)}</strong>
                      <span className="timeline-date">
                        {new Date(activity.created_at).toLocaleString('en-AU', {dateStyle:'medium', timeStyle:'short'})}
                      </span>
                    </div>
                    {activity.details && (
                      <div className="timeline-details">
                        {activity.type === 'purchase' && (
                          <p>Amount: ${activity.details.amount?.toFixed(2)} {activity.details.currency?.toUpperCase()}</p>
                        )}
                        {activity.type === 'call_received' && (
                          <p>
                            Duration: {activity.details.duration}s
                            {activity.details.loved_one_name && ` | From: ${activity.details.loved_one_name}`}
                            {activity.details.recording_url && (
                              <audio controls preload="none" style={{display:'block', marginTop:'6px', height:'28px', maxWidth:'250px'}}>
                                <source src={activity.details.recording_url} type="audio/mpeg" />
                              </audio>
                            )}
                          </p>
                        )}
                        {activity.type === 'subscription_paused' && (
                          <p>Paused for {activity.details.pause_months} month(s). Resumes: {activity.details.expected_resume_date}</p>
                        )}
                        {activity.type === 'refund_processed' && (
                          <p>Amount: ${activity.details.amount?.toFixed(2)} {activity.details.currency?.toUpperCase()} ({activity.details.refund_type})</p>
                        )}
                        {activity.type === 'onboarding_completed' && (
                          <p>Loved one: {activity.details.loved_one_name}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* RECORDINGS */}
      {activeSection === 'recordings' && (
        <div data-testid="customer-recordings">
          <h3 style={{marginBottom:'1rem'}}>Call Recordings</h3>
          {(!customer.call_logs || customer.call_logs.length === 0) ? (
            <p className="no-data">No calls recorded yet</p>
          ) : (
            <div className="table-container">
              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Caller</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {customer.call_logs.map((log, i) => (
                    <tr key={log.id || i}>
                      <td>{log.created_at ? new Date(log.created_at).toLocaleString('en-AU', {dateStyle:'medium', timeStyle:'short'}) : '-'}</td>
                      <td>{log.caller_number || '-'}</td>
                      <td>{log.recording_duration ? `${log.recording_duration}s` : '-'}</td>
                      <td><span className={`badge ${log.status === 'completed' ? 'active' : 'pending'}`}>{log.status}</span></td>
                      <td>
                        {log.recording_url ? (
                          <audio controls preload="none" style={{height:'28px', maxWidth:'250px'}}>
                            <source src={`${API_URL}/api/admin/voice/recording-proxy?url=${encodeURIComponent(log.recording_url)}&token=${localStorage.getItem('bt_admin_token')}`} type="audio/mpeg" />
                          </audio>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ORDERS & REFUNDS */}
      {activeSection === 'orders' && (
        <div data-testid="customer-orders">
          <h3 style={{marginBottom:'1rem'}}>Orders</h3>
          {customer.orders?.length > 0 ? (
            <div className="table-container">
              <table className="compact-table">
                <thead>
                  <tr><th>Date</th><th>Items</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {customer.orders.map(order => (
                    <tr key={order.id}>
                      <td>{new Date(order.created_at).toLocaleDateString('en-AU')}</td>
                      <td>{order.items?.join(', ')}</td>
                      <td>${order.amount?.toFixed(2)}</td>
                      <td><span className={`badge ${order.status}`}>{order.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No orders yet</p>
          )}

          <h3 style={{marginTop:'2rem', marginBottom:'1rem'}}>Refunds</h3>
          {customer.refunds?.length > 0 ? (
            <div className="table-container">
              <table className="compact-table">
                <thead>
                  <tr><th>Date</th><th>Amount</th><th>Type</th><th>Reason</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {customer.refunds.map((r, i) => (
                    <tr key={r.id || i}>
                      <td>{new Date(r.created_at).toLocaleDateString('en-AU')}</td>
                      <td>${r.amount?.toFixed(2)} {r.currency?.toUpperCase()}</td>
                      <td>{r.refund_type}</td>
                      <td>{r.reason || '-'}</td>
                      <td><span className={`badge ${r.status === 'succeeded' ? 'active' : 'pending'}`}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No refunds</p>
          )}
        </div>
      )}

      {/* COMMUNICATIONS */}
      {activeSection === 'comms' && (
        <div data-testid="customer-comms">
          <h3 style={{marginBottom:'1rem'}}>SMS Messages</h3>
          {customer.sms_logs?.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Date</th><th>Type</th><th>Recipient</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {customer.sms_logs.map((sms, i) => (
                    <tr key={i}>
                      <td>{sms.created_at ? new Date(sms.created_at).toLocaleString('en-AU', {dateStyle:'medium', timeStyle:'short'}) : '-'}</td>
                      <td>{sms.type || '-'}</td>
                      <td>{sms.recipient || '-'}</td>
                      <td><span className={`badge ${sms.status === 'sent' ? 'active' : 'pending'}`}>{sms.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No SMS messages sent</p>
          )}

          <h3 style={{marginTop:'2rem', marginBottom:'1rem'}}>Email Activity</h3>
          <p style={{color:'#888', fontSize:'0.9rem'}}>
            Email activity from the timeline (order confirmations, welcome emails, pause/resume/refund notifications) can be viewed in the Timeline tab.
          </p>
        </div>
      )}

      {/* FILES */}
      {activeSection === 'files' && (
        <div data-testid="customer-files">
          <div className="detail-card" style={{marginBottom:'1.5rem'}}>
            <h3>Upload Files</h3>
            <div className="upload-form">
              <select value={uploadMonth} onChange={(e) => setUploadMonth(e.target.value)}>
                <option value="">Select Month</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() - i);
                  const value = date.toISOString().slice(0, 7);
                  const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  return <option key={value} value={value}>{label}</option>;
                })}
              </select>
              <select value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
                <option value="mp3">MP3 (Audio)</option>
                <option value="pdf">PDF (Letter)</option>
              </select>
              <input
                type="file"
                accept={uploadType === 'mp3' ? '.mp3' : '.pdf'}
                onChange={(e) => setUploadFile(e.target.files[0])}
              />
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                />
                Send "Letter Ready" email notification
              </label>
              <button onClick={handleUpload}>
                <Upload size={16} /> Upload
              </button>
            </div>
          </div>

          <h3 style={{marginBottom:'1rem'}}>Downloads</h3>
          {customer.downloads?.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Month</th><th>Type</th><th>Filename</th><th>Uploaded</th></tr>
                </thead>
                <tbody>
                  {customer.downloads.map(dl => (
                    <tr key={dl.id}>
                      <td>{dl.month}</td>
                      <td>{dl.file_type === 'mp3' ? <Music size={16} /> : <FileText size={16} />} {dl.file_type.toUpperCase()}</td>
                      <td>{dl.filename}</td>
                      <td>{new Date(dl.created_at).toLocaleDateString('en-AU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No files uploaded yet</p>
          )}
        </div>
      )}
    </div>
  );
}

// Letter Preview Component — matches SVG export layout exactly
function LetterPreview({ text, lovedOne, subscriber }) {
  const title = `A Memory From ${lovedOne} for ${subscriber}`;
  
  return (
    <div data-testid="letter-preview">
      <label style={{fontWeight:600, marginBottom:'4px', display:'block', fontSize:'0.9rem', color:'#6b7280'}}>
        Letter Preview:
      </label>
      <div style={{
        width:'100%',
        aspectRatio:'190/277',
        background:'#FFFEF9',
        border:'1px solid #d1d5db',
        borderRadius:'4px',
        padding:'8% 10%',
        fontFamily:'Georgia, "Times New Roman", serif',
        position:'relative',
        overflow:'hidden',
        boxShadow:'0 2px 8px rgba(0,0,0,0.08)',
        display:'flex',
        flexDirection:'column'
      }}>
        {/* Title */}
        <div style={{textAlign:'center', marginBottom:'8px'}}>
          <span style={{fontSize:'clamp(10px, 1.4vw, 16px)', fontWeight:600, color:'#1a1a1a', letterSpacing:'0.02em'}}>
            {title}
          </span>
        </div>
        
        {/* Decorative line */}
        <div style={{
          width:'60%', height:'1px', background:'#1a1a1a', margin:'0 auto 12px', opacity:0.4
        }} />
        
        {/* Body text */}
        <div style={{
          flex:1,
          fontSize:'clamp(8px, 1vw, 12px)',
          lineHeight:'1.9',
          color:'#1a1a1a',
          whiteSpace:'pre-wrap',
          wordBreak:'break-word',
          overflow:'hidden'
        }}>
          {text || <span style={{color:'#9ca3af', fontStyle:'italic'}}>Your transcript will appear here...</span>}
        </div>
        
        {/* Footer */}
        <div style={{marginTop:'auto', paddingTop:'8px'}}>
          <div style={{
            width:'60%', height:'1px', background:'#1a1a1a', margin:'0 auto 6px', opacity:0.4
          }} />
          <div style={{textAlign:'center', fontSize:'clamp(7px, 0.8vw, 10px)', color:'#1a1a1a', opacity:0.7}}>
            With love, through Binding Thread
          </div>
        </div>
      </div>
    </div>
  );
}

// Deployment Health Check Component
function DeploymentHealthCheck({ token }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/deployment-health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealth(res.data);
      setLastChecked(new Date());
    } catch (err) {
      setHealth({ status: 'error', checks: {}, error: err.message });
    }
    setLoading(false);
  };

  useEffect(() => { checkHealth(); }, []);

  const statusIcon = (s) => {
    if (s === 'ok') return <span style={{color:'#16a34a', fontWeight:700}}>OK</span>;
    if (s === 'missing') return <span style={{color:'#f59e0b', fontWeight:700}}>MISSING</span>;
    return <span style={{color:'#ef4444', fontWeight:700}}>ERROR</span>;
  };

  return (
    <div className="settings-card" data-testid="deployment-health-check">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem'}}>
        <h3 style={{margin:0}}>Deployment Health Check</h3>
        <button onClick={checkHealth} disabled={loading} data-testid="health-check-refresh">
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> {loading ? 'Checking...' : 'Refresh'}
        </button>
      </div>
      
      {health && (
        <>
          <div style={{
            padding:'12px 16px', borderRadius:'8px', marginBottom:'16px',
            background: health.status === 'healthy' ? '#f0fdf4' : health.status === 'degraded' ? '#fefce8' : '#fef2f2',
            border: `1px solid ${health.status === 'healthy' ? '#bbf7d0' : health.status === 'degraded' ? '#fef08a' : '#fecaca'}`
          }}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <span style={{fontWeight:700, fontSize:'1.1rem', color: health.status === 'healthy' ? '#16a34a' : health.status === 'degraded' ? '#ca8a04' : '#dc2626'}}>
                {health.status === 'healthy' ? 'All Systems Operational' : health.status === 'degraded' ? 'Degraded — Some Services Missing' : 'System Error'}
              </span>
              <span style={{fontSize:'0.85rem', color:'#6b7280'}}>v{health.version || '?'}</span>
            </div>
            {lastChecked && <p style={{margin:'4px 0 0', fontSize:'0.8rem', color:'#9ca3af'}}>Last checked: {lastChecked.toLocaleTimeString()}</p>}
          </div>

          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'2px solid #e5e7eb'}}>
                <th style={{textAlign:'left', padding:'8px 12px', fontSize:'0.85rem', color:'#6b7280'}}>Service</th>
                <th style={{textAlign:'left', padding:'8px 12px', fontSize:'0.85rem', color:'#6b7280'}}>Status</th>
                <th style={{textAlign:'left', padding:'8px 12px', fontSize:'0.85rem', color:'#6b7280'}}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(health.checks || {}).map(([key, val]) => (
                <tr key={key} style={{borderBottom:'1px solid #f3f4f6'}}>
                  <td style={{padding:'8px 12px', fontWeight:600, textTransform:'capitalize'}}>{key}</td>
                  <td style={{padding:'8px 12px'}}>{statusIcon(val.status)}</td>
                  <td style={{padding:'8px 12px', fontSize:'0.9rem', color:'#6b7280'}}>{val.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default AdminPage;
